import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ReactComponent as GhostLogo } from '../../../assets/logo-4.svg';

/* Tamanho do quadrado do grid = tamanho de cada bloco das peças Tetris (mesmo quadradinho do fundo) */
const GRID_SIZE = 40;

/* Formas Tetris: cada [col, row] em unidades do grid (1 unidade = 1 quadrado do background) */
const TETRIS_SHAPES: [number, number][][] = [
  [[0, 0], [1, 0], [0, 1], [1, 1]],           // O
  [[0, 0], [0, 1], [0, 2], [0, 3]],           // I
  [[0, 0], [0, 1], [0, 2], [1, 2]],           // L
  [[1, 0], [1, 1], [1, 2], [0, 2]],           // J
  [[0, 0], [1, 0], [2, 0], [1, 1]],          // T
  [[1, 0], [2, 0], [0, 1], [1, 1]],           // S
  [[0, 0], [1, 0], [1, 1], [2, 1]],          // Z
];

/* Posições em px alinhadas ao grid (múltiplos de GRID_SIZE) para as peças “encaixarem” nos quadrados do fundo */
/* Esquerda em cima (2) | Direita (2) | Esquerda embaixo (2) | Direita embaixo = cópia da esquerda em cima (2) */
/* Distribuição equilibrada: em cima (2), embaixo (2), lado esquerdo (3), lado direito (3) — múltiplos de GRID_SIZE */
/* Esquerda: left: 0. Direita: right: 0 (fica na borda em qualquer largura). Proporção: em cima, lados, embaixo. */
/* top e left/right em múltiplos de GRID_SIZE para o contorno ficar em cima do grid */
/* Esquerda e direita: mesmo esquema — canto / mais no centro intercalado + um no final mais no centro */
const PIECE_POSITIONS: { left?: number; right?: number; rightOffset?: number; top: number; shapeIndex: number }[] = [
  { left: 0, top: 40, shapeIndex: 2 },           // esq canto
  { right: 0, rightOffset: 0, top: 40, shapeIndex: 3 },   // dir canto
  { left: 200, top: 200, shapeIndex: 4 },         // esq centro
  { right: 0, rightOffset: 200, top: 200, shapeIndex: 0 }, // dir centro
  { left: 0, top: 360, shapeIndex: 0 },           // esq canto
  { right: 0, rightOffset: 0, top: 360, shapeIndex: 4 },   // dir canto
  { left: 200, top: 520, shapeIndex: 2 },         // esq centro
  { right: 0, rightOffset: 200, top: 520, shapeIndex: 3 }, // dir centro
  { left: 0, top: 680, shapeIndex: 4 },           // esq canto
  { right: 0, rightOffset: 0, top: 680, shapeIndex: 2 },   // dir canto
  { left: 280, top: 920, shapeIndex: 3 },         // esq final mais no centro
  { right: 0, rightOffset: 280, top: 920, shapeIndex: 1 }, // dir final mais no centro (I)
];

/* Cor do grid */
const GRID_LINE_COLOR = 'rgba(168, 85, 247, 0.12)';
/* Sublinhado Tetris: mesma cor em tom mais claro, com efeito highlight */
const TETRIS_OUTLINE_COLOR = 'rgba(200, 170, 255, 0.5)';
const TETRIS_OUTLINE_GLOW = 'rgba(200, 170, 255, 0.35)';

/** Retorna o path SVG do contorno da peça Tetris (apenas as linhas que delimitam a forma, sem preenchimento) */
function getTetrisOutlinePath(shape: [number, number][], cellSize: number): string {
  const set = new Set(shape.map(([c, r]) => `${c},${r}`));
  const has = (c: number, r: number) => set.has(`${c},${r}`);
  const segs: [number, number, number, number][] = [];
  for (const [c, r] of shape) {
    const x0 = c * cellSize, y0 = r * cellSize, x1 = (c + 1) * cellSize, y1 = (r + 1) * cellSize;
    if (!has(c, r - 1)) segs.push([x0, y0, x1, y0]); // top
    if (!has(c + 1, r)) segs.push([x1, y0, x1, y1]); // right
    if (!has(c, r + 1)) segs.push([x1, y1, x0, y1]); // bottom
    if (!has(c - 1, r)) segs.push([x0, y1, x0, y0]); // left
  }
  if (segs.length === 0) return '';
  const points: [number, number][] = [[segs[0][0], segs[0][1]], [segs[0][2], segs[0][3]]];
  const used = new Set<number>([0]);
  while (used.size < segs.length) {
    const [lx, ly] = points[points.length - 1];
    let found = false;
    for (let i = 0; i < segs.length; i++) {
      if (used.has(i)) continue;
      const [a, b, c, d] = segs[i];
      if (a === lx && b === ly) { points.push([c, d]); used.add(i); found = true; break; }
      if (c === lx && d === ly) { points.push([a, b]); used.add(i); found = true; break; }
    }
    if (!found) break;
  }
  return points.length < 2 ? '' : `M ${points[0][0]} ${points[0][1]} ${points.slice(1).map(([x, y]) => `L ${x} ${y}`).join(' ')} Z`;
}

/* Monstrinhos: 6 cores sem repetir na ordem — azul, verde, vermelho, amarelo, laranja, roxo */
const MONSTER_COLORS: { filter: string }[] = [
  { filter: 'hue-rotate(200deg) saturate(1.1)' },   // azul
  { filter: 'hue-rotate(120deg) saturate(1.15)' }, // verde
  { filter: 'hue-rotate(-30deg) saturate(1.3)' },  // vermelho
  { filter: 'hue-rotate(50deg) saturate(1.2) brightness(1.05)' },  // amarelo
  { filter: 'hue-rotate(25deg) saturate(1.25)' },  // laranja
  { filter: 'hue-rotate(270deg) saturate(1.1)' },  // roxo
];

const PIECE_WIDTH = GRID_SIZE * 4;
const MONSTER_SIZE = 32;

/** Posição do monstrinho em cima da borda de cima da peça (pé na linha do topo, fora da peça). */
function getMonsterTopBorderPosition(
  minCol: number,
  maxCol: number,
  minRow: number
): { left: number; top: number } {
  const cx = ((minCol + maxCol + 1) * GRID_SIZE) / 2 - MONSTER_SIZE / 2;
  return { left: cx, top: minRow * GRID_SIZE - MONSTER_SIZE };
}

interface BackgroundTetrisProps {
  /** Quando true, mostra peças e monstrinhos só nas bordas (esq/dir), sem invadir o conteúdo central */
  edgeOnly?: boolean;
}

function BackgroundTetris({ edgeOnly = false }: BackgroundTetrisProps) {
  const [rightColLeft, setRightColLeft] = useState(280);
  useEffect(() => {
    const update = () => {
      const w = typeof window !== 'undefined' ? window.innerWidth : 360;
      setRightColLeft(Math.floor((w - PIECE_WIDTH) / GRID_SIZE) * GRID_SIZE);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const positions = edgeOnly
    ? PIECE_POSITIONS.filter(
        (pos) => pos.left === 0 || (pos.right === 0 && (pos.rightOffset ?? 0) === 0)
      )
    : PIECE_POSITIONS;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* Grid base (quadradinhos) — mesma cor usada nas peças */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(${GRID_LINE_COLOR} 1px, transparent 1px),
            linear-gradient(90deg, ${GRID_LINE_COLOR} 1px, transparent 1px)
          `,
          backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`,
        }}
      />

      {/* Destaque Tetris: só intensificar as células do grid que formam cada peça (sem bordas, sem “cortar” o grid) */}
      {positions.map((pos, pieceIndex) => {
        const shape = TETRIS_SHAPES[pos.shapeIndex];
        const monsterColor = MONSTER_COLORS[pieceIndex % MONSTER_COLORS.length];
        const minCol = Math.min(...shape.map(([c]) => c));
        const maxCol = Math.max(...shape.map(([c]) => c));
        const minRow = Math.min(...shape.map(([, r]) => r));
        const monsterPos = getMonsterTopBorderPosition(minCol, maxCol, minRow);
        const outlinePath = getTetrisOutlinePath(shape, GRID_SIZE);

        return (
          <div
            key={`piece-${pieceIndex}`}
            className="absolute progress-bg-tetris-piece"
            style={{
              left: pos.left !== undefined ? pos.left : rightColLeft - (pos.rightOffset ?? 0),
              top: pos.top,
              width: PIECE_WIDTH,
              height: PIECE_WIDTH,
            }}
          >
            {outlinePath ? (
              <svg
                width={GRID_SIZE * 4}
                height={GRID_SIZE * 4}
                className="absolute inset-0"
                style={{ overflow: 'visible' }}
              >
                <path
                  d={outlinePath}
                  fill="none"
                  stroke={TETRIS_OUTLINE_COLOR}
                  strokeWidth={1.5}
                  shapeRendering="crispEdges"
                  vectorEffect="non-scaling-stroke"
                  style={{ filter: `drop-shadow(0 0 6px ${TETRIS_OUTLINE_GLOW})` }}
                />
              </svg>
            ) : null}
            <motion.div
              className="absolute w-8 h-8 progress-bg-monster"
              style={{
                left: monsterPos.left,
                top: monsterPos.top,
                filter: monsterColor.filter,
              }}
              animate={{ y: [0, -5, 0] }}
              transition={{
                duration: 0.9 + (pieceIndex % 4) * 0.15,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: pieceIndex * 0.12,
              }}
            >
              <GhostLogo style={{ width: '100%', height: '100%' }} />
            </motion.div>
          </div>
        );
      })}
    </div>
  );
}

export default BackgroundTetris;
